import { Box } from '@mui/material';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';

interface ToastContextProps {
  showToast: (message: string, type: 'success' | 'danger' | 'warning' | 'info') => void;
}

export const ToastContext = createContext<ToastContextProps>({} as ToastContextProps);

const ToastProvider = ({ children }: { children: JSX.Element }) => {
  const showToast = useCallback((message: string, type: 'success' | 'danger' | 'warning' | 'info') => {
    const toastContainer = document.getElementById('toast-container');
    if (!toastContainer) return;

    const toastId = `toast-${Date.now()}`;
    const toast = document.createElement('div');
    toast.className = `toast align-items-center text-bg-${type} border-0 show fade`;
    toast.setAttribute('id', toastId);
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.setAttribute('aria-atomic', 'true');

    // smoothly fade in
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 0.5s ease-in-out';
    setTimeout(() => { toast.style.opacity = '1'; }, 0);

    const toastBody = document.createElement('div');
    toastBody.className = 'd-flex';

    // Add icon based on type
    let icon = '';
    switch (type) {
      case 'success':
        icon = '<i class="fa-solid fa-circle-check me-2"></i>';
        break;
      case 'danger':
        icon = '<i class="fa-solid fa-circle-xmark me-2"></i>';
        break;
      case 'warning':
        icon = '<i class="fa-solid fa-triangle-exclamation me-2"></i>';
        break;
      case 'info':
        icon = '<i class="fa-solid fa-circle-info me-2"></i>';
        break;
      default:
        break;
    }

    toastBody.innerHTML = `
      <div class="toast-body d-flex align-items-center">
      ${icon}
      ${message}
      </div>
    `;

    toast.appendChild(toastBody);
    toastContainer.appendChild(toast);

    setTimeout(() => {
      // fade out
      toast.style.opacity = '0';

      toast.addEventListener('transitionend', () => {
        // remove the toast after fade out
        toastContainer.removeChild(toast);
      });
    }, 3000);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <>
      <Box
        id="toast-container"
        className="position-fixed bottom-0 end-0 p-3"
        sx={{
          zIndex: 11,
          gap: '10px',
          display: 'flex',
          flexDirection: 'column',
          '@media (max-width: 768px)': {
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          },
        }}
      />

      <ToastContext.Provider value={value}>
        {children}
      </ToastContext.Provider>
    </>
  );
};

export default ToastProvider;

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
};
